import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getWithTokenAPI, postWithTokenAPI } from "../../util/Api";
import { version_control_get, version_control_create } from "../../util/Apiendpoits";
import { SafeToast } from "../../components/Toast/ShowToast";
import "./VersionControl.css";

const INITIAL_FORM = {
  currentVersion: "",
  forceUpdateVersion: "",
  softUpdateVersion: "",
  softUpdateMessage: "",
  forceUpdateMessage: "",
  isMaintenanceMode: false,
  maintenanceMessage: "",
};

const VersionControl = () => {
  const { token } = useSelector((state) => state.auth);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVersionControl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVersionControl = async () => {
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, version_control_get);
      if (res?.statusCode === 200 && res.data) {
        const d = res.data;
        const toStr = (v) => (v === null || v === undefined ? "" : String(v));
        setForm({
          currentVersion: toStr(d.currentVersion),
          forceUpdateVersion: toStr(d.forceUpdateVersion),
          softUpdateVersion: toStr(d.softUpdateVersion),
          softUpdateMessage: toStr(d.messages?.softUpdateMessage),
          forceUpdateMessage: toStr(d.messages?.forceUpdateMessage),
          isMaintenanceMode: !!d.maintenance?.isMaintenanceMode,
          maintenanceMessage: toStr(d.maintenance?.maintenanceMessage),
        });
      } else {
        SafeToast.error(res?.message || "Failed to load version control settings");
      }
    } catch (error) {
      console.error("Error fetching version control:", error);
      SafeToast.error("Failed to load version control settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleMaintenanceToggle = () => {
    setForm((prev) => ({ ...prev, isMaintenanceMode: !prev.isMaintenanceMode }));
    setErrors((prev) => (prev.maintenanceMessage ? { ...prev, maintenanceMessage: undefined } : prev));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form?.currentVersion?.trim()) nextErrors.currentVersion = "Current version is required";
    if (!form?.forceUpdateVersion?.trim()) nextErrors.forceUpdateVersion = "Force update version is required";
    if (!form?.softUpdateVersion?.trim()) nextErrors.softUpdateVersion = "Soft update version is required";
    if (!form?.softUpdateMessage?.trim()) nextErrors.softUpdateMessage = "Soft update message is required";
    if (!form?.forceUpdateMessage?.trim()) nextErrors.forceUpdateMessage = "Force update message is required";
    if (form?.isMaintenanceMode && !form.maintenanceMessage.trim()) {
      nextErrors.maintenanceMessage = "Maintenance message is required when maintenance mode is on";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      SafeToast.error("Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        platforms: {
          android: {
            currentVersion: form.currentVersion.trim(),
            forceUpdateVersion: form.forceUpdateVersion.trim(),
            softUpdateVersion: form.softUpdateVersion.trim(),
            softUpdateMessage: form.softUpdateMessage.trim(),
            forceUpdateMessage: form.forceUpdateMessage.trim(),
            isMaintenanceMode: form.isMaintenanceMode,
            maintenanceMessage: form.maintenanceMessage.trim(),
          },
        },
      };
      const res = await postWithTokenAPI(token, version_control_create, payload);
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SafeToast.success(res?.message || "Android version control updated successfully");
        fetchVersionControl();
      } else {
        SafeToast.error(res?.message || "Failed to update version control");
      }
    } catch (error) {
      console.error("Error saving version control:", error);
      SafeToast.error("Failed to update version control");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="vc-container">
        <div className="vc-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="vc-container">
      <div className="vc-header">
        <h2 className="vc-title">Version Control</h2>
      </div>

      <div className="vc-card">
        <h3 className="vc-card-title">Android Version Control</h3>

        <div className="vc-grid">
          <div className="form-group">
            <label>Current Version</label>
            <input
              type="text"
              className={`vc-input ${errors.currentVersion ? "input-error" : ""}`}
              value={form.currentVersion}
              onChange={handleChange("currentVersion")}
              placeholder="e.g. 1.0"
            />
            {errors.currentVersion && <span className="vc-error-text">{errors.currentVersion}</span>}
          </div>

          <div className="form-group">
            <label>Force Update Version</label>
            <input
              type="text"
              className={`vc-input ${errors.forceUpdateVersion ? "input-error" : ""}`}
              value={form.forceUpdateVersion}
              onChange={handleChange("forceUpdateVersion")}
              placeholder="e.g. 1"
            />
            {errors.forceUpdateVersion && <span className="vc-error-text">{errors.forceUpdateVersion}</span>}
          </div>

          <div className="form-group">
            <label>Soft Update Version</label>
            <input
              type="text"
              className={`vc-input ${errors.softUpdateVersion ? "input-error" : ""}`}
              value={form.softUpdateVersion}
              onChange={handleChange("softUpdateVersion")}
              placeholder="e.g. 2"
            />
            {errors.softUpdateVersion && <span className="vc-error-text">{errors.softUpdateVersion}</span>}
          </div>
        </div>

        <div className="form-group full-width">
          <label>Soft Update Message</label>
          <textarea
            className={`vc-input vc-textarea ${errors.softUpdateMessage ? "input-error" : ""}`}
            value={form.softUpdateMessage}
            onChange={handleChange("softUpdateMessage")}
            placeholder="Message shown to users when a soft update is available"
            rows={3}
          />
          {errors.softUpdateMessage && <span className="vc-error-text">{errors.softUpdateMessage}</span>}
        </div>

        <div className="form-group full-width">
          <label>Force Update Message</label>
          <textarea
            className={`vc-input vc-textarea ${errors.forceUpdateMessage ? "input-error" : ""}`}
            value={form.forceUpdateMessage}
            onChange={handleChange("forceUpdateMessage")}
            placeholder="Message shown to users when a force update is required"
            rows={3}
          />
          {errors.forceUpdateMessage && <span className="vc-error-text">{errors.forceUpdateMessage}</span>}
        </div>

        <div className="vc-maintenance-row">
          <div className="vc-maintenance-label">
            <label>Maintenance Mode</label>
            <span className="vc-maintenance-hint">
              Turn on to block Android app access with a maintenance message
            </span>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={form.isMaintenanceMode}
              onChange={handleMaintenanceToggle}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="form-group full-width">
          <label>
            Maintenance Message
            {/* {form.isMaintenanceMode && <span className="vc-required">*</span>} */}
          </label>
          <textarea
            className={`vc-input vc-textarea ${errors.maintenanceMessage ? "input-error" : ""}`}
            value={form.maintenanceMessage}
            onChange={handleChange("maintenanceMessage")}
            placeholder="Message shown to users while the app is under maintenance"
            rows={3}
            disabled={!form.isMaintenanceMode}
          />
          {errors.maintenanceMessage && <span className="vc-error-text">{errors.maintenanceMessage}</span>}
        </div>

        <div className="vc-actions">
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionControl;
